import {
  useState,
  defaultProductImage,
  useSelector,
  useNavigate,
  Fragment,
} from '../../../imports/generalImport';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  Box,
  Avatar,
  Typography,
  IconButton,
  Stack,
} from '../../../imports/muiImport';
import { categoryUrl, handleAmount } from '../../../shared/extra';
import MobileNavbar from './MobileNavbar';
import { useAuth } from '../../../hooks/contexts/AuthContext';
import MobileLogoutButton from '../mobileLogoutButton/MobileLogoutButton';
import { X } from 'lucide-react';

function stringAvatar(name) {
  const words = name?.split(' ');
  const firstInitial = words?.[0]?.[0] || '';
  const secondInitial = words?.[1]?.[0] || '';

  return {
    children: `${firstInitial}${secondInitial}`,
  };
}

function MobileHeaderDrawer(props) {
  const { isPageTitleUpdate, pageTitle } = props;
  const [open, setOpen] = useState(false);
  const categoryData = useSelector((state) => state?.MobileHomepage?.allData?.categories);
  const UserInfomation = useSelector((state) => state.UserProfile?.allData);
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const DrawerList = (
    <>
      <Stack direction={'column'}>
        <Typography variant="h6">Other Pages :</Typography>
        <List>
          {categoryData?.map((item, index) => (
            <Fragment key={`category-${item?.id * 1}`}>
              <ListItem
                disablePadding
                onClick={() => redirectToUser(`card-list?category=${categoryUrl(item?.title)}`)}
                sx={{
                  borderBottom: index + 1 !== categoryData?.length ? 1 : 0,
                  borderColor:
                    index + 1 !== categoryData?.length ? 'borderColor.main' : 'transparent',
                }}
              >
                <ListItemButton>
                  <ListItemIcon>
                    <Box
                      component="img"
                      src={item?.image || defaultProductImage}
                      width={42}
                      height={42}
                      sx={{
                        borderRadius: '50%',
                        objectFit: 'cover',
                        p: '4px',
                        border: '1px solid #deebff',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item?.title}
                    primaryTypographyProps={{
                      sx: {
                        color: 'primary.main',
                        fontWeight: 400,
                        fontSize: {
                          xs: '3.4vw',
                          sm: '1.5vw',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Fragment>
          ))}
        </List>
      </Stack>

      <Stack direction={'column'}>
        <Typography variant="h6">Other Pages :</Typography>
        <List>
          {usefulLinks?.map((item, index) => (
            <Fragment key={`other-page-${index * 1}`}>
              <ListItem
                disablePadding
                dense
                onClick={() => redirectToUser(item?.link)}
                sx={{
                  borderBottom: index + 1 !== usefulLinks.length ? 1 : 0,
                  borderColor:
                    index + 1 !== usefulLinks.length ? 'borderColor.main' : 'transparent',
                }}
              >
                <ListItemButton>
                  <ListItemText
                    primary={item?.title}
                    primaryTypographyProps={{
                      sx: {
                        color: 'dark.main',
                        fontWeight: 400,
                        fontSize: {
                          xs: '3.4vw',
                          sm: '1.5vw',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Fragment>
          ))}
        </List>
        <Divider />
      </Stack>
    </>
  );

  return (
    <>
      <MobileNavbar
        toggleDrawer={toggleDrawer}
        isPageTitleUpdate={isPageTitleUpdate}
        pageTitle={pageTitle}
      />

      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        sx={{
          position: 'relative',
          '& .MuiDrawer-paper': {
            minHeight: '100%',
            overflowY: 'auto',
            borderRadius: 0,
          },
        }}
      >
        <IconButton
          size="small"
          edge="start"
          aria-label="close model"
          sx={{
            p: 0.5,
            borderRadius: 0,
            backgroundColor: '#fff !important',

            right: 10,
            top: 10,
            position: 'absolute',
          }}
          onClick={toggleDrawer(false)}
        >
          <X
            style={{
              fontSize: 26,
              stroke: '#002865',
              strokeWidth: 3,
            }}
          />
        </IconButton>

        <Box
          sx={{
            mt: 1,
            px: 1,
            mb: 4,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: 3,

            minWidth: 240,
            maxWidth: 240,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Avatar with Badge */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                {...(isLoggedIn && UserInfomation?.data?.name
                  ? stringAvatar(UserInfomation?.data?.name)
                  : { children: 'G' })}
                sx={{
                  width: 55,
                  height: 55,
                  bgcolor: 'secondary.main',
                  fontSize: '1.5em',
                  textTransform: 'uppercase',
                  background:
                    'linear-gradient(320deg,rgba(2, 0, 36, 1) 0%, rgba(9, 9, 121, 1) 35%, rgba(0, 212, 255, 1) 100%)',
                }}
              />
            </Box>

            {/* User Name */}
            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
                fontWeight: 400,
                textAlign: 'center',
                textTransform: 'capitalize',
              }}
            >
              {isLoggedIn ? UserInfomation?.data?.name : 'Guest User'}
            </Typography>

            {/* Stats Cards */}
            {isLoggedIn && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Current Balance
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    ₹{handleAmount(UserInfomation?.data?.balance)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Lifetime Savings
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    ₹{handleAmount(UserInfomation?.data?.savings || 0)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            component={'div'}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              rowGap: 4,
            }}
            onClick={toggleDrawer(false)}
          >
            {DrawerList}
          </Box>

          {isLoggedIn && (
            <>
              <Box sx={{ width: '90%' }}>
                <MobileLogoutButton py={1.8} />
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );

  function redirectToUser(url) {
    navigate(`/${url}`);
    toggleDrawer(false);
  }
}

const usefulLinks = [
  { title: 'Blogs', link: 'blog' },
  { title: 'Corporate', link: 'corporates' },
  { title: 'About Us', link: 'aboutus' },
  { title: 'Contact Us', link: 'contact-us' },
  { title: 'How Gift Voucher Works', link: 'how-gift-card-work' },
  { title: 'Privacy Policy', link: 'privacypolicy' },
  { title: 'Terms and Conditions', link: 'termsandcondition' },
];

export default MobileHeaderDrawer;
